import React from 'react';
import { Clock, UtensilsCrossed, Zap } from 'lucide-react';
import { formatIntakeWindowLabel } from '../../../core/diet-plan/intakeTime';

/** Label + totals key, matching CompactNutritionCard's wording and order. */
const MACRO_ROWS = [
  { key: 'proteins', label: 'Protein' },
  { key: 'carbs', label: 'Carbs' },
  { key: 'fats', label: 'Fats' },
  { key: 'fiber', label: 'Fiber' },
  { key: 'sugars', label: 'Sugar' },
];

function hasAnyMacro(macros) {
  return !!macros && MACRO_ROWS.some(({ key }) => Number(macros[key]) > 0);
}

/** Card label for the meal's intake window ({ start, end } in local "HH:MM"). */
function intakeWindowLabel(intakeWindow, editable) {
  return formatIntakeWindowLabel(intakeWindow) || (editable ? 'Set intake window' : 'No intake window');
}

/**
 * Orange sidebar card shown beside a meal-slot editor row.
 * Full macro breakdown is revealed on hover/focus to keep the row layout dense.
 * `intakeWindow` and `onClick` are optional: with them the card shows the
 * meal's intake window and opens the window modal; `intakeWindow` alone shows
 * it read-only; without either it renders as before.
 */
export default function MealSlotSummaryCard({
  title,
  itemCount,
  caloriesLabel,
  macros,
  intakeWindow,
  onClick,
  disabled = false,
}) {
  const showMacros = hasAnyMacro(macros);
  const showTime = intakeWindow !== undefined;
  const clickable = typeof onClick === 'function';
  const windowLabel = showTime ? formatIntakeWindowLabel(intakeWindow) : '';

  const handleActivate = () => {
    if (clickable && !disabled) onClick();
  };

  const ariaParts = [`${title}, ${itemCount} item${itemCount !== 1 ? 's' : ''}, ${caloriesLabel}`];
  if (showMacros) {
    ariaParts.push(
      MACRO_ROWS.map(
        ({ key, label }) => `${label} ${Number(macros[key] ?? 0).toFixed(0)}g`
      ).join(', ')
    );
  }
  if (showTime) ariaParts.push(`intake window ${intakeWindowLabel(intakeWindow, clickable)}`);

  return (
    <div className="flex-shrink-0 self-center relative group">
      <div
        className={`bg-gradient-to-br from-orange-primary to-orange-600 rounded-xl shadow-md p-4 w-32 h-40 flex flex-col items-center justify-center text-white focus:outline-none focus:ring-2 focus:ring-orange-300 ${
          clickable
            ? disabled
              ? 'cursor-not-allowed opacity-80'
              : 'cursor-pointer transition-shadow hover:shadow-lg hover:ring-2 hover:ring-orange-300'
            : ''
        }`}
        role={clickable ? 'button' : undefined}
        aria-disabled={clickable && disabled ? true : undefined}
        tabIndex={clickable || showMacros ? 0 : undefined}
        onClick={clickable ? handleActivate : undefined}
        onKeyDown={
          clickable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleActivate();
                }
              }
            : undefined
        }
        aria-label={clickable || showMacros || showTime ? ariaParts.join(', ') : undefined}
      >
        <UtensilsCrossed className="w-6 h-6 mb-2" />
        <h3 className="text-sm font-bold text-center leading-tight mb-1">{title}</h3>
        <span className="text-xs opacity-90">
          {itemCount} item{itemCount !== 1 ? 's' : ''}
        </span>
        <span className="text-xs mt-1 font-medium opacity-95 flex items-center gap-0.5">
          <Zap className="w-3 h-3" />
          {caloriesLabel}
        </span>
        {showTime && (
          // One-line pill: filled when a window is set, dashed outline as the
          // call to action when it isn't (full wording lives in the aria-label).
          <span
            className={`mt-2 inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold leading-4 tabular-nums ${
              windowLabel
                ? 'bg-white/20'
                : 'border border-dashed border-white/70 opacity-90'
            }`}
          >
            <Clock className="w-3 h-3 flex-shrink-0" />
            {windowLabel || (clickable ? 'Set window' : 'No window')}
          </span>
        )}
      </div>
      {showMacros && (
        <div
          role="tooltip"
          className="pointer-events-none absolute left-full top-1/2 ml-2 z-20 w-44 -translate-y-1/2 translate-x-1 opacity-0 transition-[opacity,transform] duration-150 ease-out group-hover:translate-x-0 group-hover:opacity-100 group-focus-within:translate-x-0 group-focus-within:opacity-100"
        >
          <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
            <div className="grid grid-cols-3 gap-x-2 gap-y-2">
              {MACRO_ROWS.map(({ key, label }) => (
                <div key={key} className="flex flex-col items-center gap-0.5">
                  <span className="text-[10px] leading-none text-gray-600">{label}</span>
                  <span className="text-xs font-semibold text-gray-800">
                    {Number(macros[key] ?? 0).toFixed(0)}g
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
